import React, { memo, useCallback, useId, useMemo, useState } from 'react';
import clsx from 'clsx';
import Column from './column';
import Controls, { controlsForRow } from './controls';
import {
  IDataGridCardModeAction,
  IDataGridCardModeHeadCell,
  IDataGridColumn,
  IDataGridControl,
  IDataGridMadel,
  IDataGridRowProps,
} from '../model';
import { useTranslate } from 'hooks/use-translate';
import { CardDetailsToggle } from 'components/card-details-toggle';
import { hasCellValue, isRowIdColumn, isTextValued } from 'components/card-anatomy';

/**
 * One row of the grid laid out as a card (Phase F1, D-10 / D-53). The cells are
 * the table's own `Column` (templates, editors, permissions) rendered as
 * `div`s; the actions are the table's `Controls`. Which columns go where is
 * the config's `cardMode` declaration, resolved once per model in `CardList`.
 *
 * N1 (D-92) -- the one card anatomy of the mobile app, in three parts:
 *
 *   head = the title line and the line under it; the row menu (the overflow)
 *          at the title line's end, the only action up there;
 *   face = the fields a desktop user scans, as label / value rows (a head cell
 *          that holds a value carries its column's title as a label, R5);
 *   foot = the "details" expand at the start -- the `more` fields open between
 *          the face and the foot -- and the row's one visible action at the
 *          end: its controls, its row controls, a listed column with no title.
 *
 * The foot exists only when it has something to hold, so a card with no
 * expand and no visible action is head + face.
 */
export interface CardLayout {
  primary: IDataGridColumn | null;
  secondary: IDataGridColumn | null;
  fields: IDataGridColumn[];
  /** N1: the fields behind the "details" expand. */
  more: IDataGridColumn[];
  /** Listed columns with no title and no card label (a link, a status icon): rendered with the actions. */
  actionColumns: IDataGridColumn[];
  actions: Set<IDataGridCardModeAction>;
  headLabels: Set<IDataGridCardModeHeadCell>;
  /** N1: the card labels of untitled columns. */
  labels: Map<IDataGridColumn, string>;
  /**
   * N2b (PD-10): the config declares no `cardMode` -- the anatomy is the
   * automatic one, and the row's edit / delete controls are the card's menu.
   */
  auto: boolean;
}

const ALL_ACTIONS: IDataGridCardModeAction[] = ['controls', 'controlsDropDown', 'rowControls'];
/**
 * N1 (D-92): no inline labels by default -- the line under the title is a muted
 * subtitle (the message, the phone, the plan). An inline label ran into its
 * value in the same colour and read as one sentence, and it pushed the value off
 * the face's value column. A config whose head holds a bare date or number
 * still lists the cell (`headLabels`).
 */
const DEFAULT_HEAD_LABELS: IDataGridCardModeHeadCell[] = [];

/** A column whose title is an empty string has nothing to label its value with. */
const hasTitle = (column: IDataGridColumn) =>
  typeof column.title !== 'string' || column.title.trim() !== '';

/**
 * A declaration names a column by its alias (D-70). The parser cuts a field at
 * its first ` as `, so an expression that nests one -- `Select(k => new {
 * k.userEmployeeProfileID as id, ... }) as assignedTo` -- keeps the rest in
 * its field, and the alias is that field's end.
 */
const isColumn = (column: IDataGridColumn, name: string) =>
  column.field === name || String(column.field).endsWith(` as ${name}`);

/** A row's value of a column, under the column's alias (the end of a nested ` as `). */
const valueOf = (item: any, column: IDataGridColumn) =>
  item?.[String(column.field).split(' as ').pop() as string];

/** N2b (PD-10): the automatic anatomy's face rows; the rest is behind "details". */
export const AUTO_FACE_ROWS = 4;

/** A column the config hides in its table (an inline `display: none`) is not on the card either. */
const isVisible = (column: IDataGridColumn) =>
  (column.tdStyle as React.CSSProperties | undefined)?.display !== 'none' &&
  (column.thStyles as React.CSSProperties | undefined)?.display !== 'none';

const editorType = (column: IDataGridColumn) =>
  typeof column.editor === 'string' ? column.editor : column.editor?.type;

/**
 * A column the config sizes as narrow (a bare number `width` up to 60, the row
 * index's `20`) holds a number or an icon, never a name.
 */
const isNarrow = (column: IDataGridColumn) => {
  const width = Number(column.width);
  return Number.isFinite(width) && width > 0 && width <= 60;
};

/**
 * N2b: a text column -- a plain value (the default template, an ES6 string
 * template, an e-mail, a link), plain or edited as a string, not sized narrow.
 * Not a date, a number, a toggle, an image, html or a function template: those
 * make a poor card title.
 */
const isTextColumn = (column: IDataGridColumn) => {
  const { template } = column;
  const isText =
    template === undefined ||
    template === 'string' ||
    template === 'email' ||
    template === 'link' ||
    (typeof template === 'string' && template.includes('${'));
  const editor = editorType(column);
  return isText && (!editor || editor === 'string') && !isNarrow(column);
};

/**
 * N2b (PD-10): the anatomy of a grid whose config declares no `cardMode`. The
 * title is the record's own title field (the column the config's delete
 * dialog names the row by -- `title` by default) when the grid shows it, else
 * (N2c, SC-N2b-09) the first visible text column whose rows hold text, else
 * the first visible column whose rows hold text (a template over a name), so
 * never an id, a number or a date while such a column exists; else N2b's
 * rule -- the first visible text column (not the row's index / id), else the
 * first visible column. Then
 * up to AUTO_FACE_ROWS label / value rows from the next titled columns in
 * column order, the rest behind "details" -- the row's index / id among them,
 * never on the face (N2c). An untitled column (an icon, a button) is a card
 * action, as on a declared card.
 */
const resolveAutoLayout = (model: IDataGridMadel, data?: any[]): CardLayout => {
  const visible = model.columns.filter(isVisible);
  const titleField = model.store?.delete?.field;
  const holdsText = (column: IDataGridColumn) =>
    hasTitle(column) &&
    !isNarrow(column) &&
    !isRowIdColumn(column.field, column.title) &&
    isTextValued(data, (item) => valueOf(item, column));
  const primary =
    (titleField && visible.find((column) => isColumn(column, titleField))) ||
    visible.find((column) => isTextColumn(column) && holdsText(column)) ||
    visible.find(holdsText) ||
    visible.find(
      (column) =>
        hasTitle(column) && isTextColumn(column) && !isRowIdColumn(column.field, column.title),
    ) ||
    visible[0] ||
    null;
  const rest = visible.filter((column) => column !== primary);
  const titled = rest.filter(hasTitle);
  const face = titled
    .filter((column) => !isRowIdColumn(column.field, column.title))
    .slice(0, AUTO_FACE_ROWS);
  return {
    primary,
    secondary: null,
    fields: face,
    more: titled.filter((column) => !face.includes(column)),
    actionColumns: rest.filter((column) => !hasTitle(column)),
    actions: new Set<IDataGridCardModeAction>(ALL_ACTIONS),
    headLabels: new Set<IDataGridCardModeHeadCell>(DEFAULT_HEAD_LABELS),
    labels: new Map(),
    auto: true,
  };
};

export const resolveCardLayout = (model: IDataGridMadel, data?: any[]): CardLayout => {
  if (!model.cardMode) {
    return resolveAutoLayout(model, data);
  }
  const cardMode = model.cardMode;
  const labels = new Map<IDataGridColumn, string>();
  Object.entries(cardMode?.labels ?? {}).forEach(([name, label]) => {
    model.columns
      .filter((column) => isColumn(column, name))
      .forEach((column) => {
        labels.set(column, label);
      });
  });
  const byField = (field: string | undefined) =>
    field ? model.columns.find((column) => isColumn(column, field)) || null : null;
  const primary = byField(cardMode?.primary) || model.columns[0] || null;
  const secondary = byField(cardMode?.secondary);
  const inHead = (column: IDataGridColumn) => column === primary || column === secondary;
  const more = (cardMode?.more ?? [])
    .flatMap((field) => model.columns.filter((column) => isColumn(column, field)))
    .filter((column) => !inHead(column));
  const listed = cardMode?.fields
    ? cardMode.fields.map(byField).filter((column): column is IDataGridColumn => !!column)
    : model.columns.filter((column) => !more.includes(column));
  // N2c (T4): the row's index / id is behind "details" on a declared card too
  // (unless the declaration makes it the title)
  const rowIds = listed.filter(
    (column) => !inHead(column) && isRowIdColumn(column.field, column.title),
  );
  const rest = listed.filter((column) => !inHead(column) && !rowIds.includes(column));
  const isLabelled = (column: IDataGridColumn) => hasTitle(column) || labels.has(column);
  const fields = rest.filter(isLabelled);
  const actionColumns = rest.filter((column) => !isLabelled(column));
  const actions = new Set<IDataGridCardModeAction>(cardMode?.actions ?? ALL_ACTIONS);
  const headLabels = new Set<IDataGridCardModeHeadCell>(
    cardMode?.headLabels ?? DEFAULT_HEAD_LABELS,
  );
  return {
    primary,
    secondary,
    fields,
    more: [...rowIds.filter((column) => !more.includes(column)), ...more],
    actionColumns,
    actions,
    headLabels,
    labels,
    auto: false,
  };
};

/**
 * The column a sort state belongs to. The head's click stores the column's
 * `field`, but a config's default sort is the server expression (the column's
 * `sortable` string, or the part before ` as `): `userPatientProfileSession.entryDate`
 * for `entryDate`. Matched by any of the three, the sort control shows the
 * column instead of a blank box (PD-09, R2).
 */
export const findSortColumn = (columns: IDataGridColumn[], field: string) =>
  field
    ? columns.find(
        (column) =>
          column.field === field ||
          column.sortable === field ||
          String(column.field).split(' as ')[0] === field,
      ) || null
    : null;

/**
 * N1: a declaration's own controls replace the model's on the card; a key it
 * leaves out keeps the model's (the parser only sets the keys it was given).
 */
const cardControls = (
  model: IDataGridMadel,
  key: 'controls' | 'controlsDropDown',
): null | IDataGridControl[] => {
  const source = model.cardMode && key in model.cardMode ? model.cardMode[key] : model[key];
  return (source as null | IDataGridControl[]) ?? null;
};

/** N2b: the row controls an automatic card draws as rows of its menu. */
const isMenuControl = (control: IDataGridControl) =>
  control.type === 'edit' || control.type === 'delete';

/**
 * N2b: the row menu of an automatic card -- the config's own menu, then the
 * row's edit / delete it does not already hold (a config commonly lists
 * `edit` in both).
 */
const withMenuControls = (menu: IDataGridControl[] | null, moved: IDataGridControl[]) => {
  const own = menu || [];
  const added = moved.filter((control) => !own.some((item) => item.type === control.type));
  return own.length || added.length ? [...own, ...added] : menu;
};

interface GridCardProps {
  uKey: any;
  model: IDataGridMadel;
  item: any;
  layout: CardLayout;
  rowControls?: any;
  rowProps?: IDataGridRowProps;
}

const columnKey = (uKey: any, column: IDataGridColumn) => `${uKey}-${column.field}-${column.key}`;

/**
 * N1: a row shows its details toggle only when a detail has a value -- an
 * expand that opens onto nothing is an empty box (rubric 6). Read from the
 * row's data under the column's alias (the end of a nested ` as `).
 */
const hasValue = (item: any, column: IDataGridColumn) => hasCellValue(valueOf(item, column));

export const GridCard = memo<GridCardProps>(
  ({ uKey, model, item, layout, rowControls, rowProps }) => {
    const { t } = useTranslate();
    const moreId = useId();
    const [expanded, setExpanded] = useState(false);
    const onToggle = useCallback(() => setExpanded((value) => !value), []);
    const _rowProps = useMemo(
      () => (rowProps ? rowProps({ row: item }) : {}) as React.HTMLAttributes<HTMLElement>,
      [rowProps, item],
    );
    const { primary, secondary, actionColumns, actions, headLabels, labels } = layout;
    const more = useMemo(
      () => (layout.more.some((column) => hasValue(item, column)) ? layout.more : []),
      [layout.more, item],
    );
    // N3 (T2): a face row with no value is left off the card -- a label over
    // nothing is a gap in the card; the column stays in the details
    const face = useMemo(
      () => layout.fields.filter((column) => hasValue(item, column)),
      [layout.fields, item],
    );
    const configControls = actions.has('controls')
      ? controlsForRow(cardControls(model, 'controls'), item)
      : null;
    const rowMenu = actions.has('controlsDropDown')
      ? controlsForRow(cardControls(model, 'controlsDropDown'), item)
      : null;
    // N2b (PD-10): on an automatic card the row's edit / delete are menu rows
    // (the same controls, the same tasks); a custom control stays in the foot
    const controls = layout.auto
      ? (configControls || []).filter((c) => !isMenuControl(c))
      : configControls;
    const controlsDropDown = layout.auto
      ? withMenuControls(rowMenu, (configControls || []).filter(isMenuControl))
      : rowMenu;
    const cardRowControls = actions.has('rowControls') ? rowControls : undefined;
    const hasMenu = Boolean(controlsDropDown && controlsDropDown.length);
    const hasFootControls = Boolean((controls && controls.length) || cardRowControls);
    const hasFootActions = hasFootControls || actionColumns.length > 0;

    const titleOf = (column: IDataGridColumn) => {
      const title = labels.get(column) ?? column.title;
      return typeof title === 'string' ? t(title) : title;
    };
    const headLabel = (column: IDataGridColumn, cell: IDataGridCardModeHeadCell) =>
      headLabels.has(cell) && hasTitle(column) ? (
        <span className="data-grid-card__head-label">{titleOf(column)}</span>
      ) : null;
    const field = (column: IDataGridColumn) => {
      const label = titleOf(column);
      return (
        <div
          className={clsx('data-grid-card__field', !label && 'data-grid-card__field--wide')}
          key={columnKey(uKey, column)}
        >
          {label && <dt className="data-grid-card__label">{label}</dt>}
          <dd className="data-grid-card__value">
            <Column column={column} data={item} component="div" />
          </dd>
        </div>
      );
    };

    return (
      <div
        {..._rowProps}
        className={clsx(
          'data-grid-card',
          more.length > 0 && 'has-more',
          expanded && 'is-expanded',
          _rowProps.className,
        )}
      >
        <div className="data-grid-card__head">
          <div className="data-grid-card__titles">
            {primary && (
              <div className="data-grid-card__primary">
                {headLabel(primary, 'primary')}
                <Column
                  key={columnKey(uKey, primary)}
                  column={primary}
                  data={item}
                  component="div"
                />
              </div>
            )}
            {secondary && (
              <div className="data-grid-card__secondary">
                {headLabel(secondary, 'secondary')}
                <Column
                  key={columnKey(uKey, secondary)}
                  column={secondary}
                  data={item}
                  component="div"
                />
              </div>
            )}
          </div>
          {hasMenu && (
            <div className="data-grid-card__menu">
              <Controls
                component="div"
                controls={null}
                controlsDropDown={controlsDropDown}
                data={item}
                id={uKey}
                menuPlacement="card"
              />
            </div>
          )}
        </div>
        {face.length > 0 && <dl className="data-grid-card__fields">{face.map(field)}</dl>}
        {expanded && (
          <dl className="data-grid-card__fields data-grid-card__more" id={moreId}>
            {more.map(field)}
          </dl>
        )}
        {(more.length > 0 || hasFootActions) && (
          <div className="data-grid-card__foot">
            {more.length > 0 && (
              <CardDetailsToggle
                className="data-grid-card__toggle"
                expanded={expanded}
                onToggle={onToggle}
                controls={moreId}
              />
            )}
            {hasFootActions && (
              <div className="data-grid-card__actions">
                {actionColumns.map((column) => (
                  <Column
                    key={columnKey(uKey, column)}
                    column={column}
                    data={item}
                    component="div"
                  />
                ))}
                {hasFootControls && (
                  <Controls
                    component="div"
                    controls={controls}
                    controlsDropDown={null}
                    data={item}
                    id={uKey}
                    rowControls={cardRowControls}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);
